import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Loader2, Check, X, Layers, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";

const statusColors = { Draft: "bg-gray-100 text-gray-600", Submitted: "bg-blue-50 text-blue-700 border-blue-200", Approved: "bg-green-50 text-green-700 border-green-200", Processed: "bg-purple-50 text-purple-700 border-purple-200" };
const fmt = (v) => `R${(parseFloat(v) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;

export default function PaymentBatchesPage() {
  const { id } = useParams();
  if (id === "new") return <CreateBatch />;
  if (id) return <BatchDetail batchId={id} />;
  return <BatchList />;
}

function BatchList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => { setLoading(true); api.get(`/payment-batches?status=${filter}&limit=50`).then(r => { setItems(r.data.batches); setTotal(r.data.total); }).catch(() => toast.error("Failed")).finally(() => setLoading(false)); }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="payment-batches-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading font-bold text-2xl text-[#1F2937]">Payment Batches</h1><p className="text-sm text-gray-500 mt-1">{total} batch{total !== 1 ? "es" : ""}</p></div>
        <Button data-testid="create-batch-btn" onClick={() => navigate("/payment-batches/new")} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm"><Plus className="w-4 h-4 mr-1.5" />New Batch</Button>
      </div>
      <div className="flex gap-2 mb-4">
        {["", "Draft", "Submitted", "Approved", "Processed"].map(s => (
          <Button key={s} size="sm" variant={filter === s ? "default" : "outline"} onClick={() => setFilter(s)} className={`rounded-sm text-xs ${filter === s ? "bg-[#E63946] text-white" : ""}`}>{s || "All"}</Button>
        ))}
      </div>
      {loading ? <div className="animate-pulse h-40 bg-gray-100 rounded-sm" /> : items.length === 0 ? (
        <Card className="border rounded-sm"><CardContent className="p-12 text-center text-gray-400"><Layers className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No payment batches</p></CardContent></Card>
      ) : (
        <Card className="border rounded-sm shadow-sm"><div className="overflow-x-auto">
          <table data-testid="batches-table" className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50/50">
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Batch #</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Date</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Payments</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Total</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Created By</th>
            </tr></thead>
            <tbody>{items.map(b => (
              <tr key={b.batch_id} className="border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer" onClick={() => navigate(`/payment-batches/${b.batch_id}`)}>
                <td className="p-3 font-medium text-[#E63946]">{b.batch_number}</td>
                <td className="p-3 text-gray-600">{b.batch_date}</td>
                <td className="p-3 text-right">{b.payment_count}</td>
                <td className="p-3 text-right font-mono font-medium">{fmt(b.total_amount)}</td>
                <td className="p-3"><Badge className={`rounded-full text-xs border ${statusColors[b.status] || "bg-gray-100"}`}>{b.status}</Badge></td>
                <td className="p-3 text-gray-500">{b.created_by_name}</td>
              </tr>
            ))}</tbody>
          </table>
        </div></Card>
      )}
    </div>
  );
}

function CreateBatch() {
  const navigate = useNavigate();
  const [pendingPayments, setPending] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/supplier-payments?approval=Pending&limit=100").then(r => setPending(r.data.payments || [])).finally(() => setLoading(false)); }, []);

  const toggle = (id) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };
  const selectAll = () => { setSelected(new Set(pendingPayments.map(p => p.payment_id))); };
  const total = pendingPayments.filter(p => selected.has(p.payment_id)).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);

  const submit = async () => {
    if (selected.size === 0) { toast.error("Select at least one payment"); return; }
    setSaving(true);
    try {
      const r = await api.post("/payment-batches", { payment_ids: [...selected], notes });
      toast.success(`Batch ${r.data.batch_number} created with ${selected.size} payment(s)`);
      navigate(`/payment-batches/${r.data.batch_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <div data-testid="create-batch-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/payment-batches")} className="rounded-sm"><ArrowLeft className="w-5 h-5" /></Button>
        <h1 className="font-heading font-bold text-2xl text-[#1F2937]">Create Payment Batch</h1>
      </div>
      <p className="text-sm text-gray-500 mb-4">Select pending supplier payments to include in this batch.</p>
      {loading ? <div className="animate-pulse h-40 bg-gray-100 rounded-sm" /> : pendingPayments.length === 0 ? (
        <Card className="border rounded-sm"><CardContent className="p-12 text-center text-gray-400"><p>No pending supplier payments available</p></CardContent></Card>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3">
            <Button size="sm" variant="outline" onClick={selectAll} className="rounded-sm h-8 text-xs">Select All ({pendingPayments.length})</Button>
            <p className="text-sm font-medium">Selected: {selected.size} &middot; Total: <span className="text-[#E63946] font-mono">{fmt(total)}</span></p>
          </div>
          <Card className="border rounded-sm mb-6"><div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-gray-50/50">
                <th className="p-3 w-10"></th><th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Number</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Supplier</th>
                <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Amount</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Method</th>
              </tr></thead>
              <tbody>{pendingPayments.map(p => (
                <tr key={p.payment_id} className={`border-b border-gray-100 cursor-pointer ${selected.has(p.payment_id) ? "bg-blue-50/50" : "hover:bg-gray-50/50"}`} onClick={() => toggle(p.payment_id)}>
                  <td className="p-3 text-center"><input type="checkbox" checked={selected.has(p.payment_id)} onChange={() => toggle(p.payment_id)} className="rounded" /></td>
                  <td className="p-3 font-medium">{p.payment_number}</td>
                  <td className="p-3">{p.supplier_name}</td>
                  <td className="p-3 text-right font-mono">{fmt(p.amount)}</td>
                  <td className="p-3"><Badge className="rounded-full text-xs bg-gray-100 text-gray-600">{p.payment_method}</Badge></td>
                </tr>
              ))}</tbody>
            </table>
          </div></Card>
          <div className="mb-4"><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Notes</label><Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional batch notes" className="rounded-sm" /></div>
          <div className="flex gap-3">
            <Button data-testid="save-batch-btn" onClick={submit} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">{saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Layers className="w-4 h-4 mr-1.5" />}Create Batch</Button>
            <Button variant="outline" onClick={() => navigate("/payment-batches")} className="rounded-sm">Cancel</Button>
          </div>
        </>
      )}
    </div>
  );
}

function BatchDetail({ batchId }) {
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const load = useCallback(() => { setLoading(true); api.get(`/payment-batches/${batchId}`).then(r => setB(r.data)).catch(() => toast.error("Not found")).finally(() => setLoading(false)); }, [batchId]);
  useEffect(() => { load(); }, [load]);

  const doAction = async (action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setSubmitting(true);
    try { await api.post(`/payment-batches/${batchId}/${action}`); toast.success(`Batch ${action}d`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSubmitting(false);
  };
  const removePayment = async (payId) => {
    try { await api.post(`/payment-batches/${batchId}/remove-payment`, { payment_id: payId }); toast.success("Payment removed"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="animate-pulse h-60 bg-gray-100 rounded-sm" />;
  if (!b) return <p>Not found</p>;

  return (
    <div data-testid="batch-detail">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/payment-batches")} className="rounded-sm"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1"><h1 className="font-heading font-bold text-2xl text-[#1F2937]">{b.batch_number}</h1><p className="text-sm text-gray-500">{b.payment_count} payment(s) &middot; {b.batch_date}</p></div>
        <Badge className={`rounded-full text-sm border px-3 py-1 ${statusColors[b.status] || "bg-gray-100"}`}>{b.status}</Badge>
      </div>
      <div className="flex gap-2 mb-6">
        {b.status === "Draft" && <Button data-testid="submit-batch-btn" size="sm" onClick={() => doAction("submit")} disabled={submitting} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm"><Send className="w-4 h-4 mr-1.5" />Submit for Approval</Button>}
        {b.status === "Submitted" && <Button data-testid="approve-batch-btn" size="sm" onClick={() => doAction("approve", "Approve this batch? All payments will be approved.")} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white rounded-sm"><Check className="w-4 h-4 mr-1.5" />Approve Batch</Button>}
        {b.status === "Approved" && <Button data-testid="process-batch-btn" size="sm" onClick={() => doAction("process", "Mark as processed?")} disabled={submitting} className="bg-purple-600 hover:bg-purple-700 text-white rounded-sm"><Check className="w-4 h-4 mr-1.5" />Mark Processed</Button>}
      </div>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="border rounded-sm"><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Total Amount</p><p className="text-2xl font-bold font-mono text-[#E63946]">{fmt(b.total_amount)}</p></CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Payments</p><p className="text-2xl font-bold text-[#1F2937]">{b.payment_count}</p></CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Created By</p><p className="text-sm text-gray-600">{b.created_by_name}</p></CardContent></Card>
      </div>
      <Card className="border rounded-sm"><CardContent className="p-4">
        <h3 className="font-heading font-semibold text-sm mb-3">Payments in Batch</h3>
        <table className="w-full text-sm"><thead><tr className="border-b"><th className="p-2 text-left text-xs text-gray-500">Number</th><th className="p-2 text-left text-xs text-gray-500">Supplier</th><th className="p-2 text-right text-xs text-gray-500">Amount</th>{b.status === "Draft" && <th className="p-2 w-10"></th>}</tr></thead>
          <tbody>{(b.payments || []).map(p => (
            <tr key={p.payment_id} className="border-b border-gray-100">
              <td className="p-2 font-medium text-[#E63946] cursor-pointer" onClick={() => navigate(`/supplier-payments/${p.payment_id}`)}>{p.payment_number}</td>
              <td className="p-2">{p.supplier_name}</td>
              <td className="p-2 text-right font-mono">{fmt(p.amount)}</td>
              {b.status === "Draft" && <td className="p-2"><Button variant="ghost" size="icon" onClick={() => removePayment(p.payment_id)} className="h-7 w-7 text-gray-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button></td>}
            </tr>
          ))}</tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
